import { logError } from "@entrilia/shared/logging.ts";
import {
  financialStatementGeneralSettingsSchema,
  listGeneralSettingsSchema,
  pivotGeneralSettingsSchema,
  reportSettingsSchema,
  tabularGeneralSettingsSchema,
  tileSettingsSchema,
} from "../../../../../../../../api/schemas/biClientSchema.ts";
import {
  ConditionEqualityType,
  type ConditionConfiguration,
  type FieldConfiguration,
} from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  ConditionField,
  DimensionDescriptor,
  MeasureDescriptor,
  ReportConfiguration,
  ReportField,
  SortConfiguration,
  SortField,
} from "../../../../../../../../api/types/svTypes.ts";
import {
  AreaItemType,
  ItemDataType,
  MeasureUsage,
  MetaIcons,
  MetaItemType,
  ReportType,
  SortType,
} from "../../../../../../../../api/types/svTypes.ts";
import { convertUsDateToIso } from "../../../../../utils/dateUtils.ts";
import { getMeasureCalculateByField } from "../../../../utilities/isConfigurationValid.ts";
import { createFieldId } from "../../common/utilities/createFieldId";

export const parseReportConfiguration = (
  configuration: ReportConfiguration,
  reportType: ReportType
): ReportConfiguration => {
  const result = { ...configuration };
  switch (reportType) {
    case ReportType.CashFlow:
    case ReportType.Tabular:
      result.settings = tabularGeneralSettingsSchema.parse(configuration.settings);
      break;
    case ReportType.Pivot:
      result.settings = pivotGeneralSettingsSchema.parse(configuration.settings);
      break;
    case ReportType.List:
      result.settings = listGeneralSettingsSchema.parse(configuration.settings);
      break;
    case ReportType.Tile:
      result.settings = tileSettingsSchema.parse(configuration.settings);
      break;
    case ReportType.FinancialStatement:
      result.settings = financialStatementGeneralSettingsSchema.parse(configuration.settings);
      break;
    default:
      result.settings = { ...configuration.settings, ...reportSettingsSchema.parse(configuration.settings) };
  }
  return result;
};

export const configurationToConditions = (
  configConditions: ConditionConfiguration[],
  dimensions: DimensionDescriptor[]
): ConditionField[] => {
  try {
    const conditions = structuredClone(configConditions)
      .map((config) => {
        const dimension = dimensions.find((d) => d.name === config.filter.dimensionName);
        if (dimension) {
          const conditionField = {
            config,
            meta: dimension,
            areaItemType: AreaItemType.CONDITIONS,
          } as ConditionField;
          // backward compatibility with saved old reports
          if (conditionField.config.guid === undefined) {
            conditionField.config.guid = createFieldId(dimension.name);
          }
          conditionField.config.filter.values = conditionField.config.filter.values.map((value) =>
            convertUsDateToIso(value)
          );
          conditionField.config.filter.equalityType ??= ConditionEqualityType.Equal;
          return conditionField;
        } else {
          //eslint-disable-next-line no-console
          console.log(`Field ${config.filter.dimensionName} is not found in the dimensions' list`);
        }
        return undefined;
      })
      .filter((c): c is ConditionField => c !== undefined);
    return conditions;
  } catch (e) {
    logError(e, "configurationToConditions failed");
    return [];
  }
};

export const valueToConfiguration = (value: ReportField) => {
  return {
    ...value.config,
    calculateByField: getMeasureCalculateByField(value),
  };
};

export const configurationToSorts = (
  configSorts: SortConfiguration[],
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  reportFields: FieldConfiguration[]
) => {
  try {
    const reportFieldIds = new Set(reportFields.map((f) => f.guid));
    const sorts = configSorts
      .filter((config) => reportFieldIds.has(config.id))
      .map((config) => {
        if (config.type === SortType.Measure) {
          const measure = measures.find((m) => m.name === config.name);
          if (measure) {
            const copied: SortField = {
              config,
              meta: measure,
              areaItemType: AreaItemType.SORTS,
            };
            return copied;
          }
        }
        if (config.type === SortType.Custom) {
          const copied: SortField = {
            config,
            meta: {
              name: config.name,
              type: ItemDataType.Numeric,
              itemType: MetaItemType.CUSTOM,
              icon: MetaIcons.CustomCalculation,
              group: "",
              usage: MeasureUsage.General,
              caption: "",
            },
            areaItemType: AreaItemType.SORTS,
          };
          return copied;
        }
        const dimension = dimensions.find((d) => d.name === config.name);
        if (dimension) {
          const copied: SortField = {
            config,
            meta: dimension,
            areaItemType: AreaItemType.SORTS,
          };
          return copied;
        } else {
          //eslint-disable-next-line no-console
          console.log(`Field ${config.name} is not found in the dimensions' list`);
        }
        return undefined;
      })
      .filter((c) => c !== undefined);
    return sorts;
  } catch (e) {
    logError(e, "configurationToSorts failed");
    return [];
  }
};
